import { Component, Input, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { map, switchMap, tap } from 'rxjs/operators';
import { CheckListItemModel } from 'libs/core/src/lib/model/checklistitem.model';
import { CanBeRefundedItemResponse, CanBeRefundedResponse } from 'libs/core/src/lib/model/response/order-refund.response.models';
import { CanBeRefundedRequest, CanBeRefundedSalesDocumentItemRequest } from 'libs/core/src/lib/model/request/order-refund.request.models';
import orderBy from 'lodash-es/orderBy';
import { OrderAliasApiModel } from 'libs/core/src/lib/model/api-model/order/order-alias.api.model';
import { SalesDocumentReservationItemViewModel, SalesDocumentViewModel } from 'libs/core/src/lib/model/view-model/sales-document/sales-document.view.model';
import { SalesDocumentApiModel } from 'libs/core/src/lib/model/api-model/sales-document/sales-document.api.model';
import { ScreenViewModel } from 'libs/core/src/lib/model/view-model/screen/screen.view.model';
import { DateHelper } from 'libs/core/src/lib/date/date.helper';
import { RefundModalContent, RefundModalFailedContent, RefundModalSuccessContent, RefundTypeEnum } from '../refund-modal-content.interface';
import { BaseRefundComponent } from '../base-refund.component';
import { FlowType, LoaderStatus } from '@lib/core';

@Component({
  template: ''
})
export class RefundItemsComponent extends BaseRefundComponent implements OnInit {
  @Input() public formValues: { bookingNo?: string; email?: string };

  ngOnInit() {
    this.flowService.FlowType = FlowType.Refund;
  }

  content: RefundModalContent = {
    refundType: RefundTypeEnum.Items,
    items: []
  };

  private cinemaId: string;
  private salesDocumentId: string;
  private bookingId: string;
  private email: string;

  private refObject: {
    items: CanBeRefundedItemResponse[];
    salesDocument: SalesDocumentViewModel;
    screen: ScreenViewModel;
  };

  override readFormFields(form: FormGroup) {
    const { bookingId, email, cinemaId } = form.value;
    this.refObject = {
      items: [],
      salesDocument: null,
      screen: null
    };

    if (!bookingId) {
      this.changeLoaderState(LoaderStatus.stopped);
      return;
    }

    this.bookingId = bookingId;
    this.cinemaId = cinemaId;
    this.email = email;

    const canBeRefundedRequest = new CanBeRefundedRequest(this.email);
    const canBeRefunded = !this.cinemaId
      ? this.refundDataProvider.getOrderTransactionNumber(this.bookingId).pipe(
          map((orderAliasResponse: OrderAliasApiModel) => {
            this.cinemaId = orderAliasResponse.cinemaId;
            this.salesDocumentId = orderAliasResponse.salesDocumentId;

            canBeRefundedRequest.salesDocumentId = this.salesDocumentId;
            return canBeRefundedRequest;
          })
        )
      : of(null).pipe(
          tap(() => {
            canBeRefundedRequest.ticketNumber = this.bookingId;
          })
        );

    canBeRefunded
      .pipe(
        switchMap((request) => this.canBeRefunded(this.cinemaId, request)),
        tap((canBeRefunded: CanBeRefundedResponse) => (this.refObject.items = canBeRefunded.itemList)),
        switchMap((canBeRefunded) => this.refundDataProvider.getSalesDocumentById(this.cinemaId, canBeRefunded.salesDocumentId)),
        tap((salesDocument: SalesDocumentApiModel) => (this.refObject.salesDocument = new SalesDocumentViewModel(salesDocument))),
        switchMap((salesDocument) => this.screenDataProvider.getScreenByAndCheckIfScreeningIsEvent(this.cinemaId, salesDocument.bookings[0]?.screeningId)),
        tap((screen: ScreenViewModel) => (this.refObject.screen = screen))
      )
      .subscribe({
        next: (v) => {
          if (this.parameterControl(this.refObject)) {
            this.prepareDataToDisplayInModal(this.refObject.items, this.refObject.salesDocument, this.refObject.screen);
          }
        },
        error: (e) => this.displayFail(this.mapError(e))
      });
  }

  private parameterControl(refObject) {
    const screeningDate = refObject.screen.screeningTimeFrom;
    if (!DateHelper.isPast(screeningDate.minus({ minutes: this.allowTimeBeforeScreening }))) {
      return true;
    }

    this.displayFail();
    return false;
  }

  private prepareDataToDisplayInModal(canBeRefundedItems: CanBeRefundedItemResponse[], salesDocument: SalesDocumentViewModel, screen: ScreenViewModel): void {
    const hasRefundableItems = canBeRefundedItems?.some((o) => o.isRefundable);

    if (!hasRefundableItems) {
      this.displayFail({ isRefundable: false });
      return;
    }

    const reservationMap = new Map<string, SalesDocumentReservationItemViewModel>(salesDocument.reservationItems.map((item) => [item.ticketNumber, item]));
    const labels = {
      row: this.translateService.instant('ticketssummary.row'),
      seat: this.translateService.instant('ticketssummary.seat'),
      ticket: this.translateService.instant('basket.list.ticket')
    };

    let quantityRowCounter = 9000;

    const items = canBeRefundedItems.flatMap((response: CanBeRefundedItemResponse) => {
      const reservationItem = reservationMap.get(response.ticketMaskNumber);

      if (reservationItem) {
        return this.createReservationListItem(response, reservationItem, screen, labels);
      }

      const quantityItems = [];
      for (let i = 0; i < response.quantity; i++) {
        quantityRowCounter++;
        quantityItems.push(this.createQuantityListItem(response, quantityRowCounter, i));
      }
      return quantityItems;
    });

    this.content.items = orderBy(items, ['sortKey.row', 'sortKey.col']);
    this.displayModal('normal');
  }

  private createReservationListItem(
    response: CanBeRefundedItemResponse,
    reservationItem: SalesDocumentReservationItemViewModel,
    screen: ScreenViewModel,
    labels: { row: string; seat: string; ticket: string }
  ): CheckListItemModel {
    const rowInfo = reservationItem.row ? `, ${labels.row} <b>${reservationItem.row}</b>` : '';
    const colInfo = reservationItem.seat ? `, ${labels.seat} <b>${reservationItem.seat}</b>` : '';
    const innerInfo = `${labels.ticket} <b>${reservationItem.ticketNumber}</b>${rowInfo}${colInfo}`;

    const listItem = new CheckListItemModel(response.salesDocumentItemId, response.itemName, innerInfo);
    listItem.group = this.findSeatGroupId(screen, reservationItem) || reservationItem.ticketNumber;
    listItem.content = response;
    listItem.ticketNumber = reservationItem.ticketNumber;
    listItem['sortKey'] = {
      row: parseInt(reservationItem.row, 10) || reservationItem.row,
      col: parseInt(reservationItem.seat, 10) || parseInt(reservationItem.ticketNumber, 10) || 0
    };

    return listItem;
  }

  private createQuantityListItem(response: CanBeRefundedItemResponse, rowIndex: number, colIndex: number): CheckListItemModel {
    const listItem = new CheckListItemModel(response.salesDocumentItemId, response.itemName, '');
    listItem.content = { ...response, quantity: 1 };
    listItem.ticketNumber = '';
    listItem['sortKey'] = { row: rowIndex, col: colIndex };
    return listItem;
  }

  private findSeatGroupId(screen: ScreenViewModel, reservationItem: any): string | null {
    const rowIndex = screen.rowSymbols.indexOf(reservationItem.row);
    if (rowIndex === -1 || !screen.pseats[rowIndex]) {
      return null;
    }

    const seat = screen.pseats[rowIndex].find(
      (o) => o.symbol === reservationItem.seat || (o.groupConnectedSeats && o.groupConnectedSeats.some((g) => g.symbol === reservationItem.seat))
    );

    return seat ? seat.id : null;
  }

  afterAcceptModalClickEvent() {
    const checkedItems: CheckListItemModel[] = this.getCheckedItems(this.content.items);
    const groupedById = checkedItems.reduce(
      (accumulator, currentItem) => {
        const { id, content } = currentItem;
        const quantity = content.quantity;

        if (accumulator[id]) {
          accumulator[id].quantity += quantity;
        } else {
          accumulator[id] = { id: id, quantity: quantity };
        }

        return accumulator;
      },
      {} as { [id: string]: CanBeRefundedSalesDocumentItemRequest }
    );

    const canBeRefundedRequest = new CanBeRefundedRequest(this.email);
    canBeRefundedRequest.salesDocumentItemList = Object.values(groupedById);
    const refundedItemsCount = canBeRefundedRequest.salesDocumentItemList.length;
    const allItemsCount = this.content.items?.length ?? 0;

    this.refund(this.cinemaId, this.email, this.salesDocumentId, canBeRefundedRequest).subscribe({
      next: (refundTransactionResponse) => {
        this.googleTagManagerService?.refund(
          this.cinemaId,
          this.refObject.salesDocument,
          allItemsCount != refundedItemsCount,
          this.getCheckedItems(this.content.items).map((m) => m.ticketNumber)
        );
        this.displaySuccess({ refundedItemsCount, allItemsCount });
      },
      error: (e) => this.displayFail(this.mapError(e))
    });
  }

  private displayFail(failedContent: RefundModalFailedContent = {}) {
    this.content.failedContent = {
      ...failedContent,
      timeBeforeScreening: this.allowTimeBeforeScreening
    };

    this.displayModal('fail');
  }

  private displaySuccess(successContent?: RefundModalSuccessContent) {
    this.content.successContent = {
      ...successContent
    };

    this.displayModal('success');
  }
}
